import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import DropdownIndicator from '../../common/DropdownIndicator';
import dropdownStyleUtils from '../../../dropdownStyleUtils';
import { isMobile } from '../../../screenUtils';
import './RoleChangeDropdown.scss';

const permissionsToStringMapping = {
  invoice_approver: 'Timesheet Approver',
  manager: 'Manager',
  collaborator: 'Collaborator',
  admin: 'Admin',
  owner: 'Admin (owner)',
};

const permissionsToString = (permLevel) => permissionsToStringMapping[permLevel];

const dropDownDescriptionMapping = {
  invoice_approver: 'Can approve and request edits on timesheets. Can add other timesheet approvers.',
  manager: 'Can edit posts, hire candidates, and invite coworkers. Can view hourly rates.',
  collaborator: 'Can view. Can comment on projects and candidates. Cannot view hourly rates.',
  admin: 'Can edit posts, hire candidates, approve timesheets, and add coworkers.',
  owner: 'Owner of the project. Has the same access level as an admin.',
};

const dropDownDescription = (permLevel) => dropDownDescriptionMapping[permLevel];

const formatDropdownOptionsLabels = (option, context) => {
  const isMenuOption = context.context === 'menu';
  if (isMenuOption) {
    const isSelected = context.selectValue.some((selected) => selected.value === option.value);

    return (
      <div className={`role-menu-option menu-option-${option.value}`}>
        <div className="g-row--always g-row--between">
          <div className="g-column">
            <div className="option-label-header mb-4">{option.label}</div>
            <div className="option-label-description">
              {dropDownDescription(option.value)}
            </div>
          </div>
          {isSelected && (
            <div className="g-column">
              <i className="mx-3 check-icon fa fa-check" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return <div>{option.label}</div>;
};

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    width: state.selectProps.width,
    right: 0,
  }),
};

function RoleChangeDropdown({
  enabled,
  showRemoveOption,
  value,
  permissionOptions,
  onChange,
  handleRemove,
}) {
  const handleRoleChange = (newRole) => {
    onChange(newRole);
  };

  const renderRemoveConfirmation = (cancelRemove) => (
    <div className="remove-colleague-confirmation">
      <div className="remove-colleague-confirmation-text">Confirm removal</div>
      <div className="mr-12">
        <a
          className="remove-colleague-button"
          onClick={handleRemove}
          role="button"
          tabIndex={0}
        >
          Remove
        </a>
        <a
          className="cancel-removal-button"
          onClick={cancelRemove}
          role="button"
          tabIndex={0}
        >
          Cancel
        </a>
      </div>
    </div>
  );

  const renderRemove = (askForConfirmation) => (
    <a
      className="remove-colleague-button"
      onClick={askForConfirmation}
      role="button"
      tabIndex={0}
    >
      <i className="fa fa-trash me-3" aria-hidden="true" />
      Remove colleague
    </a>
  );

  const options = permissionOptions.map((permLevel) => ({
    value: permLevel,
    label: permissionsToString(permLevel),
  }));

  const valueForSelectComponent = {
    value,
    label: permissionsToString(value),
  };

  const menuWidth = isMobile() ? '300px' : '400px';

  function MenuList(props) {
    const [askForConfirm, setAskForConfirm] = useState(false);

    const askForConfirmation = () => setAskForConfirm(true);
    const cancelRemove = () => setAskForConfirm(false);

    return (
      <components.MenuList {...props}>
        {props.children}
        {askForConfirm
          ? renderRemoveConfirmation(cancelRemove)
          : renderRemove(askForConfirmation)}
      </components.MenuList>
    );
  }

  return (
    <div className="roles-dropdown-container">
      <Select
        classNamePrefix="roles-dropdown"
        options={options}
        placeholder="Select role"
        value={valueForSelectComponent}
        onChange={(newSelection) => handleRoleChange(newSelection.value)}
        isDisabled={!enabled}
        formatOptionLabel={formatDropdownOptionsLabels}
        styles={{ ...customStyles, ...dropdownStyleUtils }}
        width={menuWidth}
        components={{
          MenuList: showRemoveOption ? MenuList : components.MenuList,
          DropdownIndicator,
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  );
}

RoleChangeDropdown.propTypes = {
  permissionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  enabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  showRemoveOption: PropTypes.bool,
  handleRemove: PropTypes.func,
};

RoleChangeDropdown.defaultProps = {
  enabled: true,
  showRemoveOption: false,
  handleRemove: () => null,
};

export default RoleChangeDropdown;
